import { useEffect, useState, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Loader2, AlertTriangle, ArrowLeft } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from "@/components/ui/card"
import {
	fetchCustomerById,
	fetchAlyanteAgents,
	type AlyanteAgent,
} from "@/lib/api"
import { setAuthenticatedUser, clearAuthenticatedUser } from "@/lib/auth"
import { mockCurrentUser, type CurrentUser } from "@/lib/currentUser"
import type { ClientRecord } from "@/lib/mockClients"

// ----------------------------------------------------------------------------

interface AuthHandlerProps {
	className?: string
}

type AuthStatus = "loading" | "error"

// Authorized Agent and Dev IDs allowed to access the B2B Agent Portal PWA
const ALL_ACCESS_IDS = new Set(["dev-nogravity"])
const AUTHORIZED_AGENT_IDS = new Set(["dev-nogravity"])

function normalizeAgentMarker(value: string): string {
	return value.trim().toLowerCase()
}

function hasAgentTag(tags?: string[]): boolean {
	if (!Array.isArray(tags)) {
		return false
	}

	return tags.some((tag) => {
		const marker = normalizeAgentMarker(tag)
		return marker === "isagent-si" || marker === "isagent"
	})
}

function isAuthorizedAgentRecord(customer: ClientRecord | null): boolean {
	if (!customer) {
		return false
	}

	return customer.isAgent === true || hasAgentTag(customer.tags)
}

function getInitials(fullName: string): string {
	const words = fullName
		.split(" ")
		.map((value) => value.trim())
		.filter(Boolean)

	if (words.length === 0) {
		return "AG"
	}

	return words
		.slice(0, 2)
		.map((value) => value[0]?.toUpperCase() ?? "")
		.join("")
}

function extractAgentCode(tags?: string[]): string | null {
	if (!Array.isArray(tags)) return null
	for (const tag of tags) {
		const match = tag.trim().match(/^(?:agente|agent)-([a-z0-9_-]+)$/i)
		if (match && match[1]) {
			return match[1]
		}
	}
	return null
}

function buildAuthenticatedUser(
	customer: ClientRecord | null,
	fallbackId: string,
	alyanteAgent?: AlyanteAgent | null,
	agentCode?: string | null,
): CurrentUser {
	const fullName =
		alyanteAgent?.name || customer?.name || mockCurrentUser.fullName
	const derivedCode = agentCode || extractAgentCode(customer?.tags ?? [])

	return {
		id: customer ? String(customer.id) : fallbackId,
		fullName,
		email: customer?.email || mockCurrentUser.email,
		initials: getInitials(fullName),
		role: derivedCode
			? `Agente Cod. ${derivedCode}`
			: customer?.role || "Agente commerciale",
		company: "Gea Luce B2B",
		phone: customer?.phone || mockCurrentUser.phone,
		addressLine1: customer?.addressLine1 || mockCurrentUser.addressLine1,
		postalCode: customer?.postalCode || mockCurrentUser.postalCode,
		city: customer?.city || mockCurrentUser.city,
		country: mockCurrentUser.country,
		province: customer?.province || mockCurrentUser.province,
		agentCode: derivedCode ?? undefined,
		agentTag: derivedCode ? `agente-${derivedCode}` : undefined,
	}
}

function logCustomerAgentDebug(
	source: string,
	requestedId: string,
	customer: ClientRecord | null,
): void {
	if (!customer) {
		console.info(`[AuthHandler] ${source} customer debug:`, {
			requestedId,
			found: false,
		})
		return
	}

	console.info(`[AuthHandler] ${source} customer debug:`, {
		requestedId,
		found: true,
		id: customer.id,
		name: customer.name,
		email: customer.email,
		isAgent: customer.isAgent,
		role: customer.role,
		customerType: customer.customerType,
		groupName: customer.groupName,
		tags: customer.tags ?? [],
		notes: customer.notes,
	})
}

export function AuthHandler(props: AuthHandlerProps) {
	// -------------------------------------
	// Props destructuring
	// -------------------------------------

	const { className } = props

	// -------------------------------------
	// Hooks (e.g. useState, useMemo ...)
	// -------------------------------------

	const [searchParams] = useSearchParams()
	const navigate = useNavigate()
	const [status, setStatus] = useState<AuthStatus>("loading")
	const [errorMessage, setErrorMessage] = useState<string>("")
	const [manualIdInput, setManualIdInput] = useState<string>("")
	const isMountedRef = useRef(true)

	const userId = searchParams.get("id")

	// -------------------------------------
	// Effects
	// -------------------------------------

	useEffect(() => {
		isMountedRef.current = true

		processAuthentication(userId)

		return () => {
			isMountedRef.current = false
		}
	}, [userId, navigate])

	// -------------------------------------
	// Component functions
	// -------------------------------------

	async function processAuthentication(
		userId: string | null,
	): Promise<void> {
		// Clear any existing session immediately to prevent stale session login
		clearAuthenticatedUser()

		if (!userId || userId.trim().length === 0) {
			if (!isMountedRef.current) return
			setStatus("error")
			setErrorMessage(
				"Parametro ID utente mancante nella richiesta. Assicurati di accedere dall'area riservata dell'E-commerce.",
			)
			return
		}

		console.info("[AuthHandler] Starting auth verification for ID:", userId)

		try {
			const cleanId = userId.trim()
			const normalizedId = cleanId.toLowerCase()
			const isKnownAgentId = AUTHORIZED_AGENT_IDS.has(normalizedId)

			if (isKnownAgentId) {
				const authenticatedUser: CurrentUser = {
					...buildAuthenticatedUser(null, cleanId),
					id: cleanId,
					fullName: "No Gravity Dev",
					role: "Sviluppatore (Accesso Completo)",
					email: "dev@no-gravity.it",
					initials: "NG",
					canViewAll: true,
					agentCode: "707",
					agentTag: "agente-707",
				}

				setAuthenticatedUser(authenticatedUser)
				console.info(
					`[AuthHandler] Auth success! Special alias "${cleanId}" accepted (canViewAll: true), redirecting to Dashboard...`,
				)
				toast.success(`Benvenuto, ${authenticatedUser.fullName}!`, {
					description: "Accesso completato: permessi abilitati per visualizzare tutti i dati.",
				})
				navigate("/", { replace: true })
				return
			}

			// 1. Fetch customer by ID and Alyante agents directory in parallel
			let alyanteAgentsList: AlyanteAgent[] = []
			let customer: ClientRecord | null = null

			try {
				const [directCustomer, alyanteAgents] = await Promise.all([
					fetchCustomerById(cleanId).catch((err) => {
						console.warn("[AuthHandler] fetchCustomerById failed:", err)
						return null
					}),
					fetchAlyanteAgents().catch((err) => {
						console.warn("[AuthHandler] fetchAlyanteAgents failed:", err)
						return [] as AlyanteAgent[]
					}),
				])

				customer = directCustomer
				alyanteAgentsList = alyanteAgents

				console.info("[AuthHandler] Customer and Alyante agents loaded:", {
					requestedId: cleanId,
					customerFound: !!customer,
					alyanteAgentsCount: alyanteAgents.length,
				})

				if (customer) {
					logCustomerAgentDebug("Direct ID lookup", cleanId, customer)
				}
			} catch (error) {
				console.warn("[AuthHandler] Customer lookup failed:", error)
			}

			// 2. If customer was found but lacks tags, re-fetch to get complete profile
			if (customer && (!customer.tags || customer.tags.length === 0)) {
				try {
					const reFetched = await fetchCustomerById(customer.id)
					if (reFetched && reFetched.tags && reFetched.tags.length > 0) {
						customer = reFetched
						logCustomerAgentDebug(
							"Re-fetched detailed customer profile",
							customer.id,
							customer,
						)
					}
				} catch (err) {
					console.warn(
						"[AuthHandler] Re-fetching detailed customer profile failed:",
						err,
					)
				}
			}

			if (!isMountedRef.current) return

			const isAgentRole = isAuthorizedAgentRecord(customer)

			console.info("[AuthHandler] Agent markers evaluation:", {
				requestedId: cleanId,
				resolvedCustomerId: customer?.id ?? null,
				tags: customer?.tags ?? [],
				agentTagDetected: hasAgentTag(customer?.tags),
				result: isAgentRole,
			})

			if (!customer) {
				console.warn("[AuthHandler] No customer found for auth ID:", userId)
				setStatus("error")
				setErrorMessage(
					`Nessun utente trovato per l'identificativo "${userId}". Verifica che il link di accesso contenga un ID cliente valido.`,
				)
				return
			}

			if (!isAgentRole) {
				console.warn("[AuthHandler] Customer is not an authorized agent:", {
					requestedId: userId,
					resolvedCustomerId: customer.id,
					tags: customer.tags ?? [],
				})
				setStatus("error")
				setErrorMessage(
					`Accesso negato: l'utente "${customer.name || userId}" non risulta abilitato come agente e non puo accedere a questa area riservata.`,
				)
				return
			}

			// 1. Extract agent code from customer's tags (e.g. agente-50 -> "50")
			const agentCode = extractAgentCode(customer.tags)

			// 2. Fetch the complete Alyante agents directory if not already pre-loaded
			let alyanteAgent: AlyanteAgent | null = null
			try {
				const alyanteAgents =
					alyanteAgentsList.length > 0
						? alyanteAgentsList
						: await fetchAlyanteAgents()

				console.info("[AuthHandler] Alyante agents directory loaded:", {
					totalAgents: alyanteAgents.length,
					extractedCode: agentCode,
				})

				if (agentCode) {
					alyanteAgent = alyanteAgents.find((a) => a.code === agentCode) ?? null
				}

				if (!alyanteAgent && customer.name) {
					const customerNameLower = customer.name.trim().toLowerCase()
					alyanteAgent =
						alyanteAgents.find(
							(a) => a.name.trim().toLowerCase() === customerNameLower,
						) ?? null
				}
			} catch (alyanteError) {
				console.warn(
					"[AuthHandler] Failed to load Alyante agents directory:",
					alyanteError,
				)
			}

			const authenticatedUser = buildAuthenticatedUser(
				customer,
				cleanId,
				alyanteAgent,
				agentCode,
			)

			setAuthenticatedUser(authenticatedUser)
			console.info(
				`[AuthHandler] Auth success! Verified Agent "${authenticatedUser.fullName}" (Code: ${agentCode ?? "N/A"}) from Alyante ERP. Redirecting to Dashboard...`,
			)
			toast.success(`Benvenuto, ${authenticatedUser.fullName}!`, {
				description: authenticatedUser.role
					? `Accesso effettuato come ${authenticatedUser.role}`
					: "Accesso all'Area Riservata Agenti completato con successo.",
			})

			navigate("/", { replace: true })
		} catch (error) {
			console.error("[AuthHandler] Auth verification failed:", error)
			if (!isMountedRef.current) return
			setStatus("error")
			setErrorMessage(
				"Impossibile verificare l'autorizzazione utente con il server backend. Riprova più tardi.",
			)
		}
	}

	const handleReturnToEcommerce = () => {
		window.location.href = "https://www.gealuce.com"
	}

	const handleManualSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (manualIdInput.trim()) {
			navigate(`/auth?id=${encodeURIComponent(manualIdInput.trim())}`)
		}
	}

	// -------------------------------------
	// Component local variables
	// -------------------------------------

	return (
		<div
			className={`min-h-screen w-full bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4 ${className || ""}`}
		>
			{status === "loading" ? (
				<div className="flex flex-col items-center justify-center space-y-4 text-center">
					<div className="w-16 h-16 rounded-2xl bg-[#E9E21B] flex items-center justify-center shadow-lg animate-bounce">
						<span className="text-black font-extrabold text-2xl tracking-tighter">
							GL
						</span>
					</div>
					<div className="flex items-center space-x-2 text-white">
						<Loader2 className="w-5 h-5 animate-spin text-[#E9E21B]" />
						<span className="text-lg font-medium">
							Autenticazione agente in corso...
						</span>
					</div>
					<p className="text-xs text-gray-400 max-w-sm">
						Verifica credenziali B2B in corso dall'E-commerce. Verrai
						reindirizzato alla Dashboard tra un istante.
					</p>
				</div>
			) : (
				<Card className="w-full max-w-md bg-gray-900/90 border-red-500/30 text-white shadow-2xl backdrop-blur-md">
					<CardHeader className="text-center pb-2">
						<div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-2 text-red-400 border border-red-500/20">
							<AlertTriangle className="w-6 h-6" />
						</div>
						<CardTitle className="text-xl font-bold text-red-400">
							Accesso Non Autorizzato
						</CardTitle>
						<CardDescription className="text-gray-400 text-xs">
							Verifica dei permessi B2B fallita
						</CardDescription>
					</CardHeader>

					<CardContent className="space-y-6 pt-2">
						<div className="bg-red-950/40 border border-red-800/40 rounded-lg p-3.5 text-xs text-red-200 leading-relaxed">
							{errorMessage}
						</div>

						<div className="flex flex-col gap-2">
							<form onSubmit={handleManualSubmit} className="space-y-2 pt-2 border-t border-gray-800">
								<label className="text-xs text-gray-300 font-medium block">
									Oppure inserisci ID Agente o Admin:
								</label>
								<div className="flex gap-2">
									<input
										type="text"
										placeholder="Es. ID agente"
										value={manualIdInput}
										onChange={(e) => setManualIdInput(e.target.value)}
										className="flex-1 bg-gray-800 border border-gray-700 rounded-md px-3 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-[#E9E21B]"
									/>
									<Button
										type="submit"
										className="bg-[#E9E21B] text-black hover:bg-yellow-400 font-semibold px-3 py-1.5 h-auto text-xs"
									>
										Accedi
									</Button>
								</div>
							</form>

							<Button
								className="w-full bg-[#0D6EFD] hover:bg-blue-700 text-white font-medium flex items-center justify-center gap-2 mt-2"
								onClick={handleReturnToEcommerce}
							>
								<ArrowLeft className="w-4 h-4" />
								Torna a gealuce.com
							</Button>

							<Button
								variant="ghost"
								className="w-full text-gray-400 hover:text-white hover:bg-white/5 text-xs"
								onClick={() => window.location.reload()}
							>
								Riprova verifica
							</Button>
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	)
}
